/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import {
  Box,
  Button,
  TextField,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  Paper,
  Divider,
  FormControl,
  InputAdornment,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from "@mui/material"
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore"
import { toast } from 'react-toastify'
import { db } from "../../firebase"
import { tokens } from "../../theme"
import dayjs from "dayjs"
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { ANIMAL_TYPES, AnimalType } from "../../components/hooks/useParts"
import { products as legacyParts } from "./lib/common"

const SEED_DATA: Record<AnimalType, string[]> = {
  エゾシカ: legacyParts,
  ヒグマ: legacyParts.filter(p => p !== "鹿串[モモ]"),
}

interface IPartPrice {
  id: string
  part: string
  priceUnit: 'perPiece' | 'perGram'
  frozenPrice: number
  chilledPrice: number
  taxRate?: 8 | 10
  description: string
}

type PriceForm = {
  priceUnit: 'perPiece' | 'perGram'
  frozenPrice: number | ''
  chilledPrice: number | ''
  taxRate: 8 | 10
  description: string
}

const emptyPriceForm = (): PriceForm => ({
  priceUnit: 'perGram',
  frozenPrice: '',
  chilledPrice: '',
  taxRate: 8,
  description: '',
})

const Parts = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)

  const [selectedType, setSelectedType] = useState<AnimalType>("エゾシカ")
  const [parts, setParts] = useState<string[]>([])
  const [initialized, setInitialized] = useState(false)
  const [priceMap, setPriceMap] = useState<Record<string, IPartPrice>>({})

  const [openDialog, setOpenDialog] = useState(false)
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [inputName, setInputName] = useState("")
  const [nameError, setNameError] = useState("")
  const [priceForm, setPriceForm] = useState<PriceForm>(emptyPriceForm())
  const [priceErrors, setPriceErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    setInitialized(false)
    return onSnapshot(
      doc(db, 'partsSettings', selectedType),
      (snap) => {
        const data = snap.data()
        setParts(snap.exists() && Array.isArray(data?.parts) ? data.parts as string[] : [])
        setInitialized(true)
      },
      (error) => {
        toast.error(`データの読み込みに失敗しました: ${error.message}`)
        setInitialized(true)
      }
    )
  }, [selectedType])

  useEffect(() => {
    return onSnapshot(collection(db, 'partPrices'), ({ docs }) => {
      const map: Record<string, IPartPrice> = {}
      docs.forEach(d => {
        const data = d.data()
        map[data.part] = { id: d.id, ...data } as IPartPrice
      })
      setPriceMap(map)
    })
  }, [])

  const saveParts = (newParts: string[]) =>
    setDoc(doc(db, 'partsSettings', selectedType), { parts: newParts })

  const savePrice = async (partName: string, oldPartName?: string) => {
    const frozenPrice = Number(priceForm.frozenPrice)
    const chilledPrice = Number(priceForm.chilledPrice)
    if (!frozenPrice && !chilledPrice) return

    const timestamp = dayjs().format("YYYY/MM/DD HH:mm:ss")
    const data = {
      part: partName,
      priceUnit: priceForm.priceUnit,
      frozenPrice,
      chilledPrice,
      taxRate: priceForm.taxRate === 10 ? 10 : 8,
      description: priceForm.description.trim(),
      updatedAt: timestamp,
    }
    const existing = priceMap[oldPartName ?? partName]
    if (existing) {
      await updateDoc(doc(db, 'partPrices', existing.id), data)
    } else {
      await addDoc(collection(db, 'partPrices'), { ...data, createdAt: timestamp })
    }
  }

  const validateName = (): boolean => {
    if (!inputName.trim()) { setNameError("部位名は必須です"); return false }
    if (parts.some((p, i) => p === inputName.trim() && i !== editIndex)) {
      setNameError("同じ名前の部位が既に存在します"); return false
    }
    return true
  }

  const validatePrice = (): boolean => {
    const frozen = Number(priceForm.frozenPrice)
    const chilled = Number(priceForm.chilledPrice)
    const errors: Record<string, string> = {}
    if (frozen > 0 && chilled <= 0) errors.chilledPrice = 'チルド価格を入力してください'
    if (chilled > 0 && frozen <= 0) errors.frozenPrice = '冷凍価格を入力してください'
    setPriceErrors(errors)
    return Object.keys(errors).length === 0
  }

  const openAdd = () => {
    setEditIndex(null)
    setInputName("")
    setNameError("")
    setPriceForm(emptyPriceForm())
    setPriceErrors({})
    setOpenDialog(true)
  }

  const openEdit = (index: number) => {
    setEditIndex(index)
    const partName = parts[index]
    setInputName(partName)
    setNameError("")
    const existing = priceMap[partName]
    setPriceForm(existing
      ? {
        priceUnit: existing.priceUnit,
        frozenPrice: existing.frozenPrice,
        chilledPrice: existing.chilledPrice,
        taxRate: existing.taxRate === 10 ? 10 : 8,
        description: existing.description,
      }
      : emptyPriceForm()
    )
    setPriceErrors({})
    setOpenDialog(true)
  }

  const handleClose = () => {
    setOpenDialog(false)
    setEditIndex(null)
    setInputName("")
    setNameError("")
    setPriceForm(emptyPriceForm())
    setPriceErrors({})
  }

  const handleSave = async () => {
    const nameOk = validateName()
    const priceOk = validatePrice()
    if (!nameOk || !priceOk) return
    try {
      const trimmedName = inputName.trim()
      if (editIndex !== null) {
        const oldName = parts[editIndex]
        await saveParts(parts.map((p, i) => i === editIndex ? trimmedName : p))
        await savePrice(trimmedName, oldName)
        toast.success("部位を更新しました")
      } else {
        await saveParts([...parts, trimmedName])
        await savePrice(trimmedName)
        toast.success("部位を追加しました")
      }
      handleClose()
    } catch (e: any) {
      toast.error(e?.message ?? "保存に失敗しました")
    }
  }

  const handleDelete = async (index: number) => {
    const partName = parts[index]
    const hasPrice = !!priceMap[partName]
    const msg = hasPrice
      ? `「${partName}」を削除しますか？\n（価格データも合わせて削除されます）`
      : `「${partName}」を削除しますか？`
    if (!window.confirm(msg)) return
    try {
      await saveParts(parts.filter((_, i) => i !== index))
      if (hasPrice) await deleteDoc(doc(db, 'partPrices', priceMap[partName].id))
      toast.success("部位を削除しました")
    } catch (e: any) {
      toast.error(e?.message ?? "削除に失敗しました")
    }
  }

  const moveUp = async (index: number) => {
    if (index === 0) return
    const p = [...parts];
    [p[index - 1], p[index]] = [p[index], p[index - 1]]
    await saveParts(p)
  }

  const moveDown = async (index: number) => {
    if (index === parts.length - 1) return
    const p = [...parts];
    [p[index], p[index + 1]] = [p[index + 1], p[index]]
    await saveParts(p)
  }

  const handleMigrate = async () => {
    if (!window.confirm("common.ts の部位データを全獣種に一括登録します。既存のデータは上書きされます。よろしいですか？")) return
    try {
      await Promise.all(ANIMAL_TYPES.map(type =>
        setDoc(doc(db, 'partsSettings', type), { parts: SEED_DATA[type] })
      ))
      toast.success("初期データを登録しました")
    } catch (e: any) {
      toast.error(e?.message ?? "移行に失敗しました")
    }
  }

  const priceUnitLabel = priceForm.priceUnit === 'perPiece' ? '1個あたり' : 'kgあたり'

  return (
    <Box m={5}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h3">部位マスタ</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            獣種ごとに部位と価格を管理します
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          {initialized && parts.length === 0 && (
            <Button variant="outlined" color="warning" onClick={handleMigrate}>
              初期データを一括登録
            </Button>
          )}
          <Button variant="contained" color="primary" onClick={openAdd}>
            部位を追加
          </Button>
        </Box>
      </Box>

      <Tabs
        value={selectedType}
        onChange={(_, v) => setSelectedType(v as AnimalType)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {ANIMAL_TYPES.map(type => <Tab key={type} label={type} value={type} />)}
      </Tabs>

      <Paper sx={{ backgroundColor: colors.primary[400], minHeight: "60vh", maxHeight: "70vh", overflow: "auto" }}>
        {initialized && parts.length === 0 ? (
          <Box p={4} textAlign="center">
            <Typography color="text.secondary">
              部位が登録されていません。「部位を追加」または「初期データを一括登録」から登録してください。
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {parts.map((part, index) => {
              const price = priceMap[part]
              const unit = price?.priceUnit === 'perPiece' ? '/個' : '/kg'
              return (
                <Box key={`${part}-${index}`}>
                  <ListItem
                    sx={{ py: 0.75 }}
                    secondaryAction={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <IconButton size="small" disabled={index === 0} onClick={() => moveUp(index)}>
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" disabled={index === parts.length - 1} onClick={() => moveDown(index)}>
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="primary" onClick={() => openEdit(index)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(index)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <Typography variant="body2" sx={{ color: colors.grey[100], mr: 1, minWidth: 24, textAlign: 'right' }}>
                      {index + 1}.
                    </Typography>
                    <ListItemText
                      primary={part}
                      secondary={
                        price
                          ? `冷凍 ¥${price.frozenPrice.toLocaleString()}${unit} / チルド ¥${price.chilledPrice.toLocaleString()}${unit} / 税率${price.taxRate === 10 ? 10 : 8}%`
                          : "価格未設定"
                      }
                      secondaryTypographyProps={{
                        variant: 'caption',
                        color: price ? 'text.secondary' : 'warning.main',
                      }}
                    />
                  </ListItem>
                  {index < parts.length - 1 && <Divider />}
                </Box>
              )
            })}
          </List>
        )}
      </Paper>

      <Dialog open={openDialog} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>{editIndex !== null ? "部位を編集" : `部位を追加（${selectedType}）`}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="部位名"
            value={inputName}
            onChange={(e) => { setInputName(e.target.value); if (nameError) setNameError("") }}
            error={!!nameError}
            helperText={nameError}
            sx={{ mt: 1 }}
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">価格設定（任意）</Typography>

          <FormControl component="fieldset" sx={{ mt: 1, display: 'block' }}>
            <FormLabel component="legend" sx={{ fontSize: '0.75rem' }}>価格単位</FormLabel>
            <RadioGroup
              row
              value={priceForm.priceUnit}
              onChange={(e) => setPriceForm(prev => ({ ...prev, priceUnit: e.target.value as 'perPiece' | 'perGram' }))}
            >
              <FormControlLabel value="perGram" control={<Radio size="small" />} label="kg単位" />
              <FormControlLabel value="perPiece" control={<Radio size="small" />} label="1個あたり" />
            </RadioGroup>
          </FormControl>

          <FormControl component="fieldset" sx={{ mt: 1, display: 'block' }}>
            <FormLabel component="legend" sx={{ fontSize: '0.75rem' }}>消費税率</FormLabel>
            <RadioGroup
              row
              value={priceForm.taxRate}
              onChange={(e) => setPriceForm(prev => ({ ...prev, taxRate: Number(e.target.value) === 10 ? 10 : 8 }))}
            >
              <FormControlLabel value={8} control={<Radio size="small" />} label="8%（食肉等・軽減税率）" />
              <FormControlLabel value={10} control={<Radio size="small" />} label="10%（ペット用品等・標準税率）" />
            </RadioGroup>
          </FormControl>

          <Box display="flex" gap={1} mt={1}>
            <TextField
              fullWidth
              size="small"
              label={`冷凍（${priceUnitLabel}税込）`}
              type="number"
              value={priceForm.frozenPrice}
              onChange={(e) => {
                setPriceForm(prev => ({ ...prev, frozenPrice: e.target.value === '' ? '' : Number(e.target.value) }))
                if (priceErrors.frozenPrice) setPriceErrors(prev => ({ ...prev, frozenPrice: '' }))
              }}
              error={!!priceErrors.frozenPrice}
              helperText={priceErrors.frozenPrice}
              InputProps={{ startAdornment: <InputAdornment position="start">¥</InputAdornment> }}
              inputProps={{ min: 0, step: 1 }}
            />
            <TextField
              fullWidth
              size="small"
              label={`チルド（${priceUnitLabel}税込）`}
              type="number"
              value={priceForm.chilledPrice}
              onChange={(e) => {
                setPriceForm(prev => ({ ...prev, chilledPrice: e.target.value === '' ? '' : Number(e.target.value) }))
                if (priceErrors.chilledPrice) setPriceErrors(prev => ({ ...prev, chilledPrice: '' }))
              }}
              error={!!priceErrors.chilledPrice}
              helperText={priceErrors.chilledPrice}
              InputProps={{ startAdornment: <InputAdornment position="start">¥</InputAdornment> }}
              inputProps={{ min: 0, step: 1 }}
            />
          </Box>

          <TextField
            fullWidth
            size="small"
            label="備考"
            value={priceForm.description}
            onChange={(e) => setPriceForm(prev => ({ ...prev, description: e.target.value }))}
            multiline
            rows={2}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">キャンセル</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editIndex !== null ? "更新" : "追加"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Parts
